import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { theme } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { useFarmStore } from '@pages/MyFarm/store';
import { useCreateFarm, useSingleFarm, useUpdateFarm } from '@pages/MyFarm/store/myFarmHook';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary, Nullable } from '@utils/util.types';
import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useCrops, useCropsStore } from 'services/crops';
import * as Yup from 'yup';

interface CropInfo {
	crop_id: Nullable<Dictionary>;
	crop_area: string;
	crop_unit: Dictionary;
	crop_variety: Nullable<string[]>;
}

const initialValues: CropInfo = {
	crop_id: null,
	crop_area: '',
	crop_unit: {
		name: 'HA',
		id: 'hectares',
	},
	crop_variety: [''],
};

const cropValidationSchema = Yup.object().shape({
	crop_area: Yup.string().required('fieldNameError'),
	crop_unit: Yup.object().required('fieldNameError'),
	crop_id: Yup.object().nullable().required('fieldNameError'),
	crop_varieties: Yup.array().of(Yup.string()).notRequired(),
});

export function CropSelectionForm({ hideNavigationButtons }: { hideNavigationButtons?: boolean }) {
	const { t } = useTranslation();
	const { crops } = useCropsStore();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { create, loading } = useCreateFarm();
	const { update } = useUpdateFarm();
	const { id } = useParams();
	const localFarmId = localStorage.getItem('farmId') || '';
	const farmId = id || localFarmId;
	const { singleFarm } = useFarmStore();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	const cropUnitOptions: Dictionary[] = [
		{
			name: t('unitTypes.m2'),
			id: 'm2',
		},
		{
			name: t('unitTypes.hectares'),
			id: 'hectares',
		},
		{
			name: t('unitTypes.acres'),
			id: 'acres',
		},
	];

	useCrops();
	usePageTitle(`My Farm`);
	useSingleFarm(farmId || '');

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);
	const handleNext = async (values: CropInfo) => {
		const formattedValues = {
			...values,
			crop_id: values.crop_id?.id as string,
			crop_area: values.crop_area.toString() || '',
			crop_unit: values.crop_unit?.id,
		};
		const errors = await formik.validateForm();

		if (Object.keys(errors).length > 0) {
			await formik.setTouched(
				Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
			);

			return;
		}
		try {
			if (farmId) {
				await update(formattedValues, farmId); // Call update service
				showSnackbar(
					t('myFarm.updateSnackbarTitle'),
					t('myFarm.updateSnackbarDescription'),
					'success'
				);
				navigateWithLanguage(`/farmer/my-farm/edit/${farmId}/additional-information`);
			} else {
				await create(formattedValues);
				navigateWithLanguage('/farmer/my-farm/create/additional-information');
			}
		} catch (error) {
			showSnackbar('Error', 'Something went wrong. Please try again.', 'error');
		}
	};

	const formik = useFormik<CropInfo>({
		initialValues: initialValues,
		validationSchema: cropValidationSchema,
		onSubmit: handleNext,
	});

	const { setValues } = formik;

	const handleVarietyChange = (index: number, value: string) => {
		const updatedVarieties = [...(formik.values.crop_variety || [])];

		updatedVarieties[index] = value;
		formik.setFieldValue('crop_variety', updatedVarieties);
	};

	const handleVarietyUpdate = (action: 'add' | 'remove', index?: number) => {
		const updatedVarieties = [...(formik.values.crop_variety || [])];

		if (action === 'add') {
			updatedVarieties.push('');
		} else if (action === 'remove' && index !== undefined) {
			updatedVarieties.splice(index, 1);
		}
		formik.setFieldValue('crop_variety', updatedVarieties);
	};

	const handlePrev = () => {
		navigateWithLanguage('/farmer/my-farm');
	};

	const showErrors = useMemo(() => {
		return (
			(formik.touched.crop_id && formik.errors.crop_id) ||
			(formik.touched.crop_unit && formik.errors.crop_unit) ||
			(formik.touched.crop_area && formik.errors.crop_area)
		);
	}, [formik.touched, formik.errors]);

	useEffect(() => {
		if (farmId) {
			setValues({
				crop_id: singleFarm?.crop_id
					? cropsDictionary.find((crop) => crop.id === singleFarm?.crop_id)
					: null,
				crop_area: singleFarm?.crop_area || '',
				crop_unit:
					cropUnitOptions.find((unit) => unit.id === singleFarm?.crop_unit) || cropUnitOptions[0],
				crop_variety: singleFarm?.crop_variety || [''],
			});
		}
	}, [singleFarm, cropsDictionary, setValues, farmId]);

	return (
		<form
			onSubmit={formik.handleSubmit}
			className="flex flex-col  xs:mt-0 h-full gap-[20px] p-[20px] relative justify-between"
		>
			<div className=""></div>
			<div className="flex items-center justify-center sm:mt-0 mt-[20px]">
				<div className="flex flex-wrap  max-w-[500px] gap-[20px]">
					<h6 className="text-dark w-full flex items-center justify-center font-bold text-xl ">
						{t('myFarm.cropSelectionTitle')}
					</h6>
					<div className="flex flex-wrap w-full">
						<div className="w-full md:w-1/2 md:pr-[10px]">
							<TaAutocomplete
								required
								options={cropsDictionary}
								getOptionLabel={(option) => option.name}
								label={t('crops')}
								value={formik.values.crop_id as Dictionary}
								onChange={(event, value) => formik.setFieldValue('crop_id', value)}
								error={Boolean(formik.touched.crop_id && formik.errors.crop_id)}
							/>
						</div>
						<div className="w-1/2 md:w-1/4 pr-[10px] md:mt-0 mt-[20px]">
							<TaInput
								required
								id="crop_area"
								label={t('myFarm.crop_area')}
								size="small"
								type="number"
								value={formik.values.crop_area}
								onChange={(e) => {
									const value = e.target.value;

									if (/^\d*\.?\d*$/.test(value)) {
										formik.setFieldValue('crop_area', value);
									}
								}}
								inputProps={{
									min: 0,
									step: 'any',
								}}
								error={formik.errors.crop_area}
								touched={formik.touched.crop_area}
							/>
						</div>
						<div className="w-1/2 md:w-1/4 md:mt-0 mt-[20px]">
							<TaAutocomplete
								required
								options={cropUnitOptions}
								getOptionLabel={(option) => option.name}
								label={t('unit')}
								value={formik.values.crop_unit as Dictionary}
								onChange={(event, value) => formik.setFieldValue('crop_unit', value)}
								error={Boolean(formik.touched.crop_unit && formik.errors.crop_unit)}
							/>
						</div>
					</div>
					{showErrors && (
						<div>
							{formik.touched.crop_id && formik.errors.crop_id && (
								<ErrorMessage
									message={t(formik.errors.crop_id as string, { fieldName: t('crops') })}
								/>
							)}
							{formik.touched.crop_unit && formik.errors.crop_unit && (
								<ErrorMessage
									message={t(formik.errors.crop_unit as string, {
										fieldName: t('unit'),
									})}
								/>
							)}
							{formik.touched.crop_area && formik.errors.crop_area && (
								<ErrorMessage
									message={t(formik.errors.crop_area as string, {
										fieldName: t('myFarm.crop_area'),
									})}
								/>
							)}
						</div>
					)}
					<div className="w-full">
						{formik.values?.crop_variety?.map((variety, index) => (
							<div key={index} className="relative flex items-center gap-4 mb-4">
								<div className="relative w-full">
									<TaInput
										id={`crop_variety_${index}`}
										label={t('myFarm.variety')}
										size="small"
										value={variety}
										onChange={(e) => handleVarietyChange(index, e.target.value)}
										error={
											formik.touched.crop_variety && Boolean(formik.errors.crop_variety?.[index])
										}
									/>
									{index > 0 && (
										<div
											className="absolute top-[-10px] right-[-10px] flex items-center justify-center rounded-lg bg-red-500 p-1.5 text-white cursor-pointer h-6 w-6 text-xs"
											onClick={() => handleVarietyUpdate('remove', index)}
										>
											<CloseIcon className="!text-xs" />
										</div>
									)}
								</div>
							</div>
						))}
						<div className="w-full flex justify-start">
							<Button
								className="text-[#343A40] font-black"
								onClick={() => handleVarietyUpdate('add')}
								sx={buttonStyle}
								variant="contained"
								startIcon={<AddIcon />}
							>
								{t('myFarm.addVariety')}
							</Button>
						</div>
					</div>
				</div>
			</div>

			<div className="w-full flex  md:flex-row md:gap-[0] gap-[20px] flex-col  justify-between">
				<div className="flex items-center justify-center md:hidden">
					<p className="text-secondary text-center font-normal text-base">
						{t('myFarm.considerationText')}
					</p>
				</div>
				{!hideNavigationButtons && (
					<div className="flex items-center gap-[20px] justify-between w-full">
						<Button
							onClick={handlePrev}
							classes={{
								root: 'w-[111px]',
							}}
							sx={buttonStyle}
							variant="contained"
							startIcon={<ArrowBackIosIcon />}
						>
							{t('previous')}
						</Button>
						<div className="flex items-center gap-[20px]">
							<p className="text-secondary text-center font-normal text-base md:block hidden">
								{t('myFarm.considerationText')}
							</p>
							<Button
								disabled={loading}
								onClick={() => handleNext(formik.values)}
								classes={{
									root: 'w-[111px]',
								}}
								variant="contained"
								endIcon={<ArrowForwardIosIcon />}
							>
								{t('next')}
							</Button>
						</div>
					</div>
				)}
			</div>
		</form>
	);
}

const buttonStyle = {
	backgroundColor: theme.palette.custom.contrastTextWhite,
	color: theme.palette.custom.darkGray,
};
